import React, { useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useToast } from './ToastContainer';
import Loader from './Loader';

import Logo from '../assets/logo.svg';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background-color: #172A2A;
  border-radius: 24px;
  padding: 40px;
  width: 90%;
  max-width: 600px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const HeaderSection = styled.div`
  background-color: #1F3737;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  align-self: center;
  width: fit-content;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const Title = styled.h2`
  font-family: Ubuntu, sans-serif;
  font-weight: 500;
  font-size: 24px;
  color: #FFFFFF;
  text-align: center;
  margin: 0;
`;

const DropZone = styled.div<{ $isDragOver: boolean; $hasFile: boolean }>`
  border: 2px dashed ${props => 
    props.$hasFile ? '#24BA7D' : 
    props.$isDragOver ? '#609F94' : '#284640'
  };
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: ${props => 
    props.$hasFile ? 'rgba(36, 186, 125, 0.1)' : 
    props.$isDragOver ? 'rgba(96, 159, 148, 0.1)' : 'transparent'
  };
  
  &:hover {
    border-color: ${props => props.$hasFile ? '#24BA7D' : '#609F94'};
    background-color: ${props => props.$hasFile ? 'rgba(36, 186, 125, 0.1)' : 'rgba(96, 159, 148, 0.05)'};
  }
`;

const DropZoneIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #1F3737;
  border-radius: 12px;
`;

const DropZoneTitle = styled.h3`
  font-family: Ubuntu, sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: #FFFFFF;
  margin: 0 0 8px 0;
`;

const DropZoneDescription = styled.p`
  font-family: Ubuntu, sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #609F94;
  margin: 0 0 16px 0;
`;

const FileInfo = styled.div`
  background-color: #1F3737;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const FileIcon = styled.div`
  width: 40px;
  height: 40px;
  background-color: #24BA7D;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FileDetails = styled.div`
  flex: 1;
`;

const FileName = styled.div`
  font-family: Ubuntu, sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #FFFFFF;
  margin-bottom: 4px;
`;

const FileSize = styled.div`
  font-family: Ubuntu, sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #609F94;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const ExampleLinkContainer = styled.div`
  text-align: center;
  padding: 16px;
  background-color: #1F3737;
  border-radius: 8px;
  margin: 16px 0;
`;

const ExampleLinkText = styled.p`
  font-family: Ubuntu, sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #609F94;
  margin: 0 0 8px 0;
`;

const ExampleLink = styled.a`
  font-family: Ubuntu, sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #24BA7D;
  text-decoration: none;
  border-bottom: 1px solid #24BA7D;
  padding-bottom: 1px;
  
  &:hover {
    color: #20A568;
    border-bottom-color: #20A568;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ $primary?: boolean }>`
  background-color: ${props => props.$primary ? '#24BA7D' : 'transparent'};
  border: 2px solid ${props => props.$primary ? '#24BA7D' : '#284640'};
  border-radius: 9999px;
  padding: 12px 24px;
  color: ${props => props.$primary ? '#FFFFFF' : '#609F94'};
  font-family: Ubuntu, sans-serif;
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  
  &:hover {
    background-color: ${props => props.$primary ? '#20A568' : 'rgba(96, 159, 148, 0.1)'};
    border-color: ${props => props.$primary ? '#20A568' : '#609F94'};
    color: ${props => props.$primary ? '#FFFFFF' : '#FFFFFF'};
  }
  
  &:disabled {
    background-color: #4F6F66;
    border-color: #4F6F66;
    color: #8B9494;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 14px;
    height: 2px;
    background-color: #8B9494;
  }
  
  &::before {
    transform: rotate(45deg);
  }
  
  &::after {
    transform: rotate(-45deg);
  }
  
  &:hover {
    &::before,
    &::after {
      background-color: #A5B1B1;
    }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(23, 42, 42, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 24px;
  z-index: 100;
  backdrop-filter: blur(2px);
`;

const HiddenFileInput = styled.input`
  display: none;
`;

// Иконки
const UploadIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 21.3333V8M16 8L10.6667 13.3333M16 8L21.3333 13.3333" stroke="#609F94" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M26.6667 21.3333V25.3333C26.6667 26.0406 26.3857 26.7189 25.8856 27.219C25.3855 27.719 24.7072 28 24 28H8C7.29276 28 6.61448 27.719 6.11438 27.219C5.61428 26.7189 5.33333 26.0406 5.33333 25.3333V21.3333" stroke="#609F94" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ExcelFileIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#FFFFFF"/>
    <path d="M14 2V8H20" fill="#172A2A"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 4H14M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4M5.33333 4V2.66667C5.33333 2 6 1.33333 6.66667 1.33333H9.33333C10 1.33333 10.6667 2 10.6667 2.66667V4" stroke="#8B9494" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface ExcelImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<void>;
}

const ExcelImportModal: React.FC<ExcelImportModalProps> = ({
  isOpen,
  onClose,
  onImport
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showError } = useToast();

  const validateFile = (file: File): boolean => {
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    
    const allowedExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      showError('Поддерживаются только файлы Excel (.xlsx, .xls) и CSV');
      return false;
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB
      showError('Размер файла не должен превышать 10MB');
      return false;
    }
    
    return true;
  };

  const handleFileSelect = (file: File) => {
    if (validateFile(file)) {
      setSelectedFile(file);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setIsLoading(true);
    try {
      await onImport(selectedFile);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    } catch (error) {
      // Ошибка уже обработана в onImport
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose} />

        <HeaderSection>
          <IconWrapper>
            <img src={Logo} alt="Logo" />
          </IconWrapper>
        </HeaderSection>

        <Title>Импорт из Excel файла</Title>

        {!selectedFile && (
          <DropZone
            $isDragOver={isDragOver}
            $hasFile={!!selectedFile}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleUploadClick}
          >
            <DropZoneIcon>
              <UploadIcon />
            </DropZoneIcon>
            <DropZoneTitle>
              Перетащите файл сюда
            </DropZoneTitle>
            <DropZoneDescription>
              или нажмите для выбора файла Excel (.xlsx, .xls) или CSV
            </DropZoneDescription>
          </DropZone>
        )}

        {selectedFile && (
          <FileInfo>
            <FileIcon>
              <ExcelFileIcon />
            </FileIcon>
            <FileDetails>
              <FileName>{selectedFile.name}</FileName>
              <FileSize>{formatFileSize(selectedFile.size)}</FileSize>
            </FileDetails>
            <RemoveFileButton onClick={handleRemoveFile}>
              <TrashIcon />
            </RemoveFileButton>
          </FileInfo>
        )}

        <ExampleLinkContainer>
          <ExampleLinkText>
            Нужен пример шаблона для импорта?
          </ExampleLinkText>
          <ExampleLink
            href="/src/assets/example.xlsx"
            download="example.xlsx"
          >
            Скачать пример шаблона
          </ExampleLink>
        </ExampleLinkContainer>

        <ButtonsContainer>
          <Button onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
          <Button 
            $primary 
            onClick={handleImport} 
            disabled={!selectedFile || isLoading}
          >
            {isLoading ? 'Импорт...' : 'Импортировать'}
          </Button>
        </ButtonsContainer>

        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileInputChange}
        />

        {isLoading && (
          <LoadingOverlay>
            <Loader text="Импорт файла..." />
          </LoadingOverlay>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default ExcelImportModal;